import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useExternalNetworkStats } from '../api/useExternalNetworkStats';
import { useInternalNetworkStats } from '../api/useInternalNetworkStats';
import {
  formatTargetScopeKey,
  TARGET_TYPE,
  type TargetScope,
} from '../../utils/target-scope.utils';
import {
  useTargetVisitsStore,
  type TargetMetricsSnapshot,
} from '../../store/targetVisitsStore';

function sumFindingsBySeverity({
  critical,
  high,
  medium,
  low,
  info = 0,
}: {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info?: number;
}) {
  return critical + high + medium + low + info;
}

function buildExternalSnapshot(
  stats: NonNullable<ReturnType<typeof useExternalNetworkStats>['stats']>,
): TargetMetricsSnapshot {
  const totalAssets =
    stats.totalNetblocks +
    stats.totalDomains +
    stats.totalHosts +
    stats.totalGateways +
    stats.totalServices +
    stats.totalSoftware;

  return {
    signalsCount: stats.totalSignals,
    findingsCount: sumFindingsBySeverity(stats.findingsCountsBySeverity),
    assetsCount: totalAssets,
    domainsInScope: stats.domainsInScope,
  };
}

export function useNetworkDataSidebarBadges({
  surfaceId,
  targetType,
}: {
  surfaceId: string | null;
  targetType: TargetTypeType;
}) {
  const isExternal = targetType === TARGET_TYPE.EXTERNAL_NETWORK;
  const isInternal = targetType === TARGET_TYPE.INTERNAL_NETWORK;
  const isNetworkTarget = isExternal || isInternal;

  const { stats: externalStats } = useExternalNetworkStats(
    isExternal ? surfaceId : null,
  );
  const { stats: internalStats } = useInternalNetworkStats(
    isInternal ? surfaceId : null,
  );

  const scopeKey = surfaceId
    ? formatTargetScopeKey({
        targetId: surfaceId,
        targetType,
      } satisfies TargetScope)
    : null;

  const previousMetrics = useTargetVisitsStore((state) =>
    scopeKey ? state.metricsHistory[scopeKey] : undefined,
  );

  if (!isNetworkTarget || !surfaceId || !scopeKey || !previousMetrics) {
    return {
      signalsHasNew: false,
      findingsHasNew: false,
    };
  }

  const currentMetrics = isExternal
    ? externalStats
      ? buildExternalSnapshot(externalStats)
      : null
    : internalStats
      ? {
          signalsCount: internalStats.signalCount,
          findingsCount: internalStats.openFindingCount,
          crownJewelCount: internalStats.crownJewelCount,
          ownedPrincipalCount: internalStats.ownedPrincipalCount,
          onlineEnrollmentCount: internalStats.onlineEnrollmentCount,
        }
      : null;

  if (!currentMetrics) {
    return {
      signalsHasNew: false,
      findingsHasNew: false,
    };
  }

  return {
    signalsHasNew: currentMetrics.signalsCount > previousMetrics.signalsCount,
    findingsHasNew:
      currentMetrics.findingsCount > previousMetrics.findingsCount,
  };
}
